import {
  Newspaper, Users, Radar, Gauge, CalendarDays, MessageSquare, Tag, Gift, Bell, Search, Network, Map,
} from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { FEATURES } from "@/data/content";

const ICONS = {
  newspaper: Newspaper, users: Users, radar: Radar, gauge: Gauge, calendar: CalendarDays,
  message: MessageSquare, tag: Tag, gift: Gift, bell: Bell, search: Search, network: Network, map: Map,
};

function FeatureCard({ f, i }) {
  const Icon = ICONS[f.icon] || Newspaper;
  if (f.featured) {
    return (
      <motion.div
        whileHover={{ y: -6 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="group relative flex h-full flex-col justify-between overflow-hidden rounded-2xl border border-gold/35 bg-gradient-to-br from-card via-card to-night-soft p-7 sm:col-span-1 lg:col-span-2"
        data-testid="feature-card-fil-dactualite"
      >
        <div className="flex items-start justify-between gap-6">
          <div>
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-gold/30 bg-gold/10 text-gold transition-all duration-300 group-hover:bg-gold group-hover:text-night">
              <Newspaper className="h-5 w-5" strokeWidth={1.8} />
            </span>
            <h3 className="mt-5 font-heading text-xl font-bold text-foreground">{f.title}</h3>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
          </div>
          <div className="hidden w-56 flex-col gap-2 rounded-xl border border-border bg-night-soft/60 p-3 sm:flex" aria-hidden="true">
            {[1, 2, 3].map((n) => (
              <div key={n} className="flex items-center gap-2">
                <span className="h-6 w-6 shrink-0 rounded-full bg-gold/25" />
                <span className="flex-1">
                  <span className="block h-1.5 w-3/4 rounded bg-foreground/15" />
                  <span className="mt-1 block h-1.5 w-1/2 rounded bg-foreground/10" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    );
  }
  return (
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="group h-full rounded-2xl border border-border bg-card p-6 transition-colors duration-300 hover:border-gold/50"
      data-testid={`feature-card-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gold/25 bg-gold/5 text-gold transition-all duration-300 group-hover:scale-110 group-hover:bg-gold group-hover:text-night">
        <Icon className="h-5 w-5" strokeWidth={1.8} />
      </span>
      <h3 className="mt-5 font-heading text-base font-bold text-foreground sm:text-lg">{f.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.desc}</p>
    </motion.div>
  );
}

export default function Features() {
  return (
    <section id="fonctionnalites" className="py-16 md:py-24 lg:py-32" data-testid="features-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="Fonctionnalités"
          title="Une application complète, pensée pour les membres."
          sub="Douze outils au service du réseau et de la passion, réunis dans une seule application."
        />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 0.07}>
              <FeatureCard f={f} i={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
